import { useState, useEffect } from 'react';

export type TelegramTheme = 'light' | 'dark';

export const useTelegramTheme = (): TelegramTheme => {
  const [theme, setTheme] = useState<TelegramTheme>('dark');

  useEffect(() => {
    const updateTheme = () => {
      if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
        const colorScheme = window.Telegram.WebApp.colorScheme;
        setTheme(colorScheme || 'dark');
      }
    };

    updateTheme();

    // Listen for theme changes
    if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
      // Check if there's a theme change event
      // Telegram WebApp doesn't have a direct event, so we'll check periodically
      // But we can also listen to window theme changes
      const interval = setInterval(updateTheme, 500);
      
      // Also listen to storage events (if theme is stored)
      const handleStorageChange = () => updateTheme();
      window.addEventListener('storage', handleStorageChange);
      
      return () => {
        clearInterval(interval);
        window.removeEventListener('storage', handleStorageChange);
      };
    }
  }, []);

  return theme;
};

// Utility function to get input classes based on theme
export const getInputClasses = (theme: TelegramTheme, additionalClasses: string = ''): string => {
  const baseClasses = 'w-full p-3 border rounded-lg text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all';
  
  if (theme === 'light') {
    // Light theme: light background, dark text
    return `${baseClasses} bg-white border-gray-300 text-gray-900 placeholder:text-gray-500 ${additionalClasses}`;
  } else {
    // Dark theme: dark background, light text
    return `${baseClasses} bg-gray-900/50 border-gray-700 text-white placeholder:text-gray-500 ${additionalClasses}`;
  }
};

// Utility function to get input classes for larger inputs (p-4, rounded-xl)
export const getInputClassesLarge = (theme: TelegramTheme, additionalClasses: string = ''): string => {
  const baseClasses = 'w-full p-4 border rounded-xl text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all';
  
  if (theme === 'light') {
    // Light theme: light background, dark text
    return `${baseClasses} bg-white border-gray-300 text-gray-900 placeholder:text-gray-500 ${additionalClasses}`;
  } else {
    // Dark theme: dark background, light text
    return `${baseClasses} bg-gray-900/50 border-gray-700 text-white placeholder:text-gray-500 ${additionalClasses}`;
  }
};
